<template>
  <div class="flex flex-col gap-4">
    <div v-if="showSections">
      <div
        v-for="(section, sectionIndex) in sections"
        :key="`search-section-${sectionIndex}`"
      >
        <template v-if="sectionHasResults(section)">
          <vetster-search-result-info-heading
            v-bind="section.title.bindings"
            v-on="section.title.listeners"
          />
          <div class="flex flex-col space-y-4 flex-1 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 sm:pb-0 mt-6 lg:mt-5">
            <vetster-search-tile
              v-for="(result, resultIndex) in section.results"
              :id="`in-state-search-result-${resultIndex}`"
              :key="result.feature.uuid"
              :is-in-state="result.is_in_state"
              :is-top-pick="isFirstDisplayedResult(resultIndex, sectionIndex) && showTopPick(result)"
              :is-vcpr-enabled="result.is_vcpr_enabled"
              :max-timeslots-count="maxTimeslotsCount"
              :result="result"
              :timezone="timezone"
              @selected="timeslotSelected"
            />
          </div>
        </template>
      </div>

      <vetster-out-of-state-info-slide-over ref="outOfStateInfoSlideOver" />
    </div>
    <div
      v-else
      class="flex flex-col space-y-4 flex-1 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 sm:pb-0 mt-6 lg:mt-5"
    >
      <vetster-search-tile
        v-for="(result, index) in results"
        :key="result.feature.uuid"
        :is-in-state="result.is_in_state"
        :is-top-pick="index === 0 && showTopPick(result)"
        :is-vcpr-enabled="result.is_vcpr_enabled"
        :max-timeslots-count="maxTimeslotsCount"
        :result="result"
        :timezone="timezone"
        @selected="timeslotSelected"
      />
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import maxBy from 'lodash/maxBy';
import size from 'lodash/size';
import { mapGetters } from 'vuex';

import VetsterOutOfStateInfoSlideOver from '@/brand-components/GuidedSearch/VetsterOutOfStateInfoSlideOver';
import VetsterSearchResultInfoHeading from '@/brand-components/GuidedSearch/VetsterSearchResultInfoHeading';
import VetsterSearchTile from '@/brand-components/GuidedSearch/VetsterSearchTile';

library.add(faCircleInfo);

export default {
  name: 'VetsterSearchResults',

  components: {
    VetsterSearchTile,
    VetsterOutOfStateInfoSlideOver,
    VetsterSearchResultInfoHeading,
  },

  props: {
    maxResults: {
      type: Number,
      default: 6,
    },
    results: {
      type: Array,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapGetters('search', ['isVcprSearch']),

    areAllResultsInState() {
      return size(this.inStateResults) === size(this.results);
    },

    hasInStateResults() {
      return size(this.inStateResults) > 0;
    },

    hasMultipleResults() {
      return size(this.results) > 1;
    },

    hasResults() {
      return size(this.results) > 0;
    },

    hasHalfInStateResults() {
      return size(this.inStateResults) === (this.maxResults / 2);
    },

    inStateResults() {
      return this.results.filter((result) => result.is_in_state);
    },

    inStateSectionResults() {
      return this.hasHalfInStateResults ? this.inStateResults : this.results;
    },

    inStateSectionTitle() {
      if (!this.isVcprSearch) {
        return {};
      }

      if (!this.hasInStateResults) {
        return this.getNoInStateResultsTitle();
      }

      if (this.areAllResultsInState || this.hasHalfInStateResults) {
        return this.getAvailableVetsInStateTitle();
      }

      return this.getSomeOutOfStateResultsTitle();
    },

    isVetCardBadgeExperiment() {
      return this.$statsig.ready
        && this.$statsig.checkGate('search_vet_card_badges')
        && this.$statsig.getExperiment('expt_search_vet_card_badges').get('value', false);
    },

    maxTimeslotsCount() {
      return this.hasResults
        ? maxBy(this.results, (result) => result.timeslots.length).timeslots.length
        : 0;
    },

    outOfStateResults() {
      return this.results.filter((result) => !result.is_in_state);
    },

    outOfStateSectionResults() {
      return this.hasHalfInStateResults ? this.outOfStateResults : [];
    },

    secondSectionTitle() {
      return {
        bindings: {
          class: 'mt-8 lg:mt-12',
          text: this.$t('search.want-to-explore-more-vets'),
          icon: ['fal', 'circle-info'],
        },
        listeners: { 'icon-clicked': this.openOutOfStateInfoSlideOver },
      };
    },

    sections() {
      return [
        {
          title: this.inStateSectionTitle,
          results: this.inStateSectionResults,
        },
        {
          title: this.secondSectionTitle,
          results: this.outOfStateSectionResults,
        },
      ];
    },

    showSections() {
      return this.$statsig?.ready && this.$statsig.checkGate('search_out_of_state_vcpr_behaviour');
    },
  },

  methods: {
    getAvailableVetsInStateTitle() {
      return {
        bindings: {
          text: this.$t('search.available-vets-in-your-state'),
        },
      };
    },

    getNoInStateResultsTitle() {
      return {
        bindings: {
          text: this.$t('search.there-are-no-vets-currently'),
          icon: ['fal', 'circle-info'],
        },
        listeners: { 'icon-clicked': this.openOutOfStateInfoSlideOver },
      };
    },

    getSomeOutOfStateResultsTitle() {
      return {
        bindings: {
          text: this.$t('search.some-vets-might-be-out-of-your-state'),
          icon: ['fal', 'circle-info'],
        },
        listeners: { 'icon-clicked': this.openOutOfStateInfoSlideOver },
      };
    },

    isFirstDisplayedResult(resultIndex, sectionIndex) {
      return resultIndex === 0 && sectionIndex === 0;
    },

    openOutOfStateInfoSlideOver() {
      this.$refs.outOfStateInfoSlideOver.open();
    },

    sectionHasResults(section) {
      return size(section.results) > 0;
    },

    showTopPick(result) {
      return result.is_in_state
        && this.hasMultipleResults
        && this.isVetCardBadgeExperiment;
    },

    timeslotSelected(result, timeslot) {
      this.$emit('selected', result, timeslot);
    },
  },
};
</script>
